// TMOS Create Issue Page
// Date: November 15, 2025
// Purpose: Page for creating new issues

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { issueService, CreateIssueRequest } from '@/services/issueService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import api from '@/services/api';

interface IssueCategory {
  id: string;
  name: string;
}

export const CreateIssuePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<IssueCategory[]>([]);
  const [formData, setFormData] = useState<CreateIssueRequest>({
    description: '',
    issue_category_id: undefined,
    urgency: 'MEDIUM',
    impact: 'MEDIUM',
    privacy: 'PRIVATE',
    target_type: 'PLATFORM',
    target_id: undefined,
    target_tenant_id: undefined
  });

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await api.get('/generic/issue_categories?page=1&limit=1000');
        setCategories(response.data.data || []);
      } catch (error) {
        console.error('Error loading issue categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Read query parameters and pre-fill form (for REPORT_BUGS and other auto-parameter scenarios)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryId = params.get('issue_category_id');
    const targetType = params.get('target_type');
    const targetId = params.get('target_id');
    
    if (categoryId || targetType || targetId) {
      setFormData(prev => ({
        ...prev,
        ...(categoryId && { issue_category_id: categoryId }),
        ...(targetType && { target_type: targetType as any }),
        ...(targetId && { target_id: targetId })
      }));
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error(t('issues.descriptionRequired') || 'Description is required');
      return;
    }

    try {
      setLoading(true);
      const issue = await issueService.createIssue(formData);
      toast.success(t('issues.createdSuccess') || 'Issue created successfully');
      navigate(`/issues/${issue.id}`);
    } catch (error: any) {
      console.error('Error creating issue:', error);
      toast.error(error.response?.data?.message || 'Failed to create issue');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateIssueRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/issues')}
              className="mr-2"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              {t('common.back') || 'Back'}
            </Button>
            <ExclamationTriangleIcon className="w-8 h-8 text-[var(--color-warning)]" />
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                {t('issues.createIssue') || 'Create Issue'}
              </h1>
              <p className="text-[var(--color-secondary-text)]">
                {t('issues.createIssueDescription') || 'Report a new issue or request support'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <Card variant="outline" padding="lg">
          <CardHeader>
            <CardTitle>{t('issues.issueDetails') || 'Issue Details'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('issues.description') || 'Description'} <span className="text-[var(--color-error)]">*</span>
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  placeholder={t('issues.descriptionPlaceholder') || 'Describe the issue in detail'}
                />
                <p className="mt-1 text-xs text-[var(--color-secondary-text)]">
                  {t('issues.autoTitleHint') || 'A title is auto-generated from this description for lists and navigation.'}
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('issues.category') || 'Category'}
                </label>
                <select
                  value={formData.issue_category_id || ''}
                  onChange={(e) => handleChange('issue_category_id', e.target.value || undefined)}
                  className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                >
                  <option value="">{t('issues.selectCategory') || 'Select Category (Optional)'}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Grid for Urgency, Impact, Privacy, Target Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Urgency */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.urgency') || 'Urgency'}
                  </label>
                  <select
                    value={formData.urgency}
                    onChange={(e) => handleChange('urgency', e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="LOW">{t('issues.low') || 'Low'}</option>
                    <option value="MEDIUM">{t('issues.medium') || 'Medium'}</option>
                    <option value="HIGH">{t('issues.high') || 'High'}</option>
                    <option value="CRITICAL">{t('issues.critical') || 'Critical'}</option>
                  </select>
                </div>

                {/* Impact */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.impact') || 'Impact'}
                  </label>
                  <select
                    value={formData.impact}
                    onChange={(e) => handleChange('impact', e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="LOW">{t('issues.low') || 'Low'}</option>
                    <option value="MEDIUM">{t('issues.medium') || 'Medium'}</option>
                    <option value="HIGH">{t('issues.high') || 'High'}</option>
                    <option value="CRITICAL">{t('issues.critical') || 'Critical'}</option>
                  </select>
                </div>

                {/* Privacy */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.privacy') || 'Privacy'}
                  </label>
                  <select
                    value={formData.privacy}
                    onChange={(e) => handleChange('privacy', e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="PRIVATE">{t('issues.private') || 'Private'}</option>
                    <option value="PUBLIC">{t('issues.public') || 'Public'}</option>
                  </select>
                </div>

                {/* Target Type */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.targetType') || 'Target Type'}
                  </label>
                  <select
                    value={formData.target_type}
                    onChange={(e) => handleChange('target_type', e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="PLATFORM">{t('issues.platform') || 'Platform'}</option>
                    <option value="TENANT_ADMIN">{t('issues.tenantAdmin') || 'Tenant Admin'}</option>
                    <option value="PROGRAM">{t('issues.program') || 'Program'}</option>
                    <option value="PROJECT">{t('issues.project') || 'Project'}</option>
                    <option value="USER">{t('issues.user') || 'User'}</option>
                    <option value="TRANSFORMATION">{t('issues.transformation') || 'Transformation'}</option>
                  </select>
                </div>
              </div>

              {/* Target ID (optional, shown when target_type is not PLATFORM) */}
              {formData.target_type !== 'PLATFORM' && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.targetId') || 'Target ID'} ({t('issues.optional') || 'Optional'})
                  </label>
                  <input
                    type="text"
                    value={formData.target_id || ''}
                    onChange={(e) => handleChange('target_id', e.target.value || undefined)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    placeholder={t('issues.targetIdPlaceholder') || 'Enter target entity ID'}
                  />
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/issues')}
                  disabled={loading}
                >
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
                  disabled={loading}
                >
                  {loading ? (t('common.creating') || 'Creating...') : (t('issues.createIssue') || 'Create Issue')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

